import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Clapperboard, Package, Sparkles, Loader2, Check, Wand2, Compass,
  AlertTriangle, RefreshCw, Download, UserCircle, Captions,
  CalendarClock, ChevronUp, ChevronDown, Video,
} from "lucide-react";
import type { Persona, Product, Offer, NarrativeAngle, BrollExpressRun, BrollExpressVideo } from "@shared/schema";
import { PickerSelect } from "@/components/picker-select";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PastRunsSection, formatRunWhen } from "@/components/past-runs-section";
import { groupAnglesByFamily } from "@/lib/angle-groups";

type RunWithVideos = { run: BrollExpressRun; videos: BrollExpressVideo[] };

const ACTIVE_STATUSES = new Set(["pending", "rendering"]);
// Statuses that mean a run still needs to stay pinned in the current section.
// awaiting_approval is a legacy status from the removed script-review gate —
// pre-removal rows auto-continue on the next server boot.
const IN_PROGRESS_STATUSES = new Set(["pending", "awaiting_approval", "rendering"]);
// Caption states that mean the burned-in caption pass is still running — the
// tile keeps polling so the captioned URL swaps in when it lands.
const CAPTIONS_IN_FLIGHT = new Set(["pending", "generating"]);

function videoNeedsPolling(v: BrollExpressVideo): boolean {
  if (ACTIVE_STATUSES.has(v.status)) return true;
  return v.status === "ready" && CAPTIONS_IN_FLIGHT.has(v.captionedVideoStatus ?? "");
}

function statusMeta(video: BrollExpressVideo): { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Video; spinning: boolean } {
  switch (video.status) {
    case "pending":
      // "pending" covers the script write (auto-approved) and the brief
      // window before the render task is submitted.
      return video.scriptApprovedAt
        ? { label: "Preparing render", variant: "secondary", icon: Loader2, spinning: true }
        : { label: "Writing script", variant: "secondary", icon: Sparkles, spinning: true };
    case "awaiting_approval":
      // Legacy parked rows (pre gate-removal) — they resume on server boot.
      return { label: "Resuming…", variant: "secondary", icon: Loader2, spinning: true };
    case "rendering":
      return { label: "Rendering video", variant: "secondary", icon: Loader2, spinning: true };
    case "ready":
      return { label: "Ready", variant: "default", icon: Check, spinning: false };
    case "failed":
      return { label: "Failed", variant: "destructive", icon: AlertTriangle, spinning: false };
    default:
      return { label: video.status, variant: "outline", icon: Video, spinning: false };
  }
}

export default function BrollExpressPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();

  const [selectedPersonaId, setSelectedPersonaId] = useState<string>("");
  // The run promotes EITHER a product OR an offer (service) — same segmented
  // toggle as UGC Express. Switching modes clears the other selection so the
  // server always receives exactly one promoted item.
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">("product");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedOfferId, setSelectedOfferId] = useState<string>("");
  // Narrative angle: pick a saved angle (filtered to the chosen persona +
  // promoted item) OR write a custom concept that the server transforms into
  // a saved angle before the run starts (Ads Express pattern).
  const [angleMode, setAngleMode] = useState<"saved" | "custom">("saved");
  const [angleId, setAngleId] = useState<string>("");
  const [customConcept, setCustomConcept] = useState<string>("");

  const isOfferMode = promotedItemType === "offer";

  const { data: products = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: offers = [], isLoading: offersLoading } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });
  const { data: personas = [], isLoading: personasLoading } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const { data: angles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });

  // Saved angles are persona + promoted-item specific — only surface angles
  // generated for the selected pair so the script never runs on a mismatched
  // angle. Offer mode matches on the angle's offeringId (the narrative layer
  // stores offer angles under offeringId, not productId).
  const availableAngles = useMemo(
    () =>
      angles.filter((a) => {
        if (selectedPersonaId && a.personaId !== selectedPersonaId) return false;
        if (isOfferMode) return !selectedOfferId || a.offeringId === selectedOfferId;
        return !selectedProductId || a.productId === selectedProductId;
      }),
    [angles, selectedPersonaId, selectedProductId, selectedOfferId, isOfferMode],
  );

  const angleGroups = useMemo(
    () =>
      groupAnglesByFamily(availableAngles).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({
          value: a.id,
          label: a.name,
          description: a.summary ?? null,
        })),
      })),
    [availableAngles],
  );

  // If the persona/product change invalidates the chosen angle, clear it so
  // we never quietly send a mismatched angleId on Generate.
  useEffect(() => {
    if (angleId && !availableAngles.some((a) => a.id === angleId)) {
      setAngleId("");
    }
  }, [angleId, availableAngles]);

  // The main view shows the current run(s), hydrated from the server on mount
  // so in-progress runs (including ones parked at script approval) survive
  // refresh pinned up top.
  const [focusedRuns, setFocusedRuns] = useState<BrollExpressRun[]>([]);
  const { data: activeData, isLoading: activeLoading } = useQuery<{ runs: RunWithVideos[] }>({
    queryKey: [`/api/brands/${brandId}/broll-express/runs/active`],
    enabled: !!brandId,
  });
  useEffect(() => {
    setFocusedRuns([]);
  }, [brandId]);
  useEffect(() => {
    if (focusedRuns.length === 0 && activeData?.runs?.length) {
      setFocusedRuns(activeData.runs.map((r) => r.run));
      for (const r of activeData.runs) {
        queryClient.setQueryData(
          [`/api/brands/${brandId}/broll-express/runs/${r.run.id}/videos`],
          r.videos,
        );
      }
    }
  }, [activeData, focusedRuns, brandId]);

  const createRun = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/broll-express/runs`, {
        personaId: selectedPersonaId,
        promotedItemType,
        productId: isOfferMode ? undefined : selectedProductId,
        offerId: isOfferMode ? selectedOfferId : undefined,
        narrativeAngleId: angleMode === "saved" ? angleId : undefined,
        customConcept: angleMode === "custom" ? customConcept.trim() : undefined,
      });
      return (await res.json()) as RunWithVideos;
    },
    onSuccess: (result) => {
      // The new run goes up top; prior focused runs that still need attention
      // stay pinned alongside it instead of dropping into Past Runs mid-flight.
      setFocusedRuns((prev) => {
        const stillInProgress = prev.filter((run) => {
          if (run.id === result.run.id) return false;
          const vids =
            queryClient.getQueryData<BrollExpressVideo[]>([
              `/api/brands/${brandId}/broll-express/runs/${run.id}/videos`,
            ]) ?? [];
          return vids.some((v) => IN_PROGRESS_STATUSES.has(v.status));
        });
        return [result.run, ...stillInProgress];
      });
      queryClient.setQueryData([`/api/brands/${brandId}/broll-express/runs/${result.run.id}/videos`], result.videos);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/broll-express/runs`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/broll-express/runs/active`] });
      // A custom concept was just saved as a new angle server-side — refresh
      // the saved-angle list so it shows up for future runs.
      if (angleMode === "custom") {
        queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
        setCustomConcept("");
      }
      toast({
        title: "Generating B-roll video",
        description: "Writing the script now — the render starts automatically.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Could not start", description: e?.message || "Failed to create run", variant: "destructive" });
    },
  });

  const canGenerate =
    !!selectedPersonaId &&
    (isOfferMode ? !!selectedOfferId : !!selectedProductId) &&
    (angleMode === "saved" ? !!angleId : !!customConcept.trim()) &&
    !createRun.isPending;

  return (
    <div className="mx-auto max-w-5xl space-y-5 px-8 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Clapperboard className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">B-roll Express</h1>
          <p className="text-sm text-muted-foreground">
            Pick a persona, a product or offer, and a narrative angle to generate a captioned 30-second vertical B-roll video.
          </p>
        </div>
      </div>

      {/* Builder */}
      <Card>
        <CardContent className="space-y-5 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
                <Wand2 className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Pick a persona and a product or offer, then choose the angle driving the script
                </p>
              </div>
            </div>
            {/* Product/Offer segmented toggle — same pattern as UGC Express */}
            <div
              className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 backdrop-blur shadow-sm"
              data-testid="toggle-promoted-item"
            >
              <Button
                type="button"
                size="sm"
                variant={isOfferMode ? "ghost" : "default"}
                className="h-7 px-3 text-[11px] gap-1 rounded-full"
                onClick={() => {
                  if (createRun.isPending) return;
                  setPromotedItemType("product");
                  setSelectedOfferId("");
                }}
                disabled={createRun.isPending}
                data-testid="toggle-promoted-product"
              >
                <Package className="h-3 w-3" /> Product
              </Button>
              <Button
                type="button"
                size="sm"
                variant={isOfferMode ? "default" : "ghost"}
                className="h-7 px-3 text-[11px] gap-1 rounded-full"
                onClick={() => {
                  if (createRun.isPending) return;
                  setPromotedItemType("offer");
                  setSelectedProductId("");
                }}
                disabled={createRun.isPending}
                data-testid="toggle-promoted-offer"
              >
                <Sparkles className="h-3 w-3" /> Offer
              </Button>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <PickerSelect
              icon={<UserCircle className="h-4 w-4" />}
              label="Persona"
              placeholder={personasLoading ? "Loading…" : "Choose persona"}
              value={selectedPersonaId}
              onChange={setSelectedPersonaId}
              options={personas.map((p) => ({ value: p.id, label: p.name, imageUrl: p.imageUrl }))}
              testId="select-persona"
              searchable
              disabled={personasLoading || createRun.isPending}
            />
            {/* Product / Offer picker — matches UGC Express */}
            {isOfferMode ? (
              <PickerSelect
                icon={<Sparkles className="h-4 w-4" />}
                label="Offer"
                placeholder={offers.length === 0 ? "No offers yet — add one in Offers" : "Choose offer"}
                value={selectedOfferId}
                onChange={(v) => {
                  setSelectedOfferId(v);
                  setSelectedProductId("");
                }}
                options={offers.map((o) => ({
                  value: o.id,
                  label: o.name,
                  description: (o as any).shortDescription || (o as any).summary || null,
                }))}
                testId="select-offer"
                searchable
                disabled={offersLoading || offers.length === 0 || createRun.isPending}
              />
            ) : (
              <PickerSelect
                icon={<Package className="h-4 w-4" />}
                label="Product"
                placeholder="Choose product"
                value={selectedProductId}
                onChange={(v) => {
                  setSelectedProductId(v);
                  setSelectedOfferId("");
                }}
                options={[...products]
                  .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                  .map((p) => {
                    const media = (p as { mediaJson?: string[] | null }).mediaJson;
                    const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
                    return { value: p.id, label: p.name, imageUrl: first };
                  })}
                testId="select-product"
                searchable
                disabled={productsLoading || createRun.isPending}
              />
            )}
          </div>

          {/* Narrative angle: saved vs custom (Ads Express pattern) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Compass className="h-4 w-4 text-muted-foreground" /> Narrative angle
              </label>
              <div
                className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 backdrop-blur shadow-sm"
                data-testid="toggle-angle-mode"
              >
                <Button
                  type="button"
                  size="sm"
                  variant={angleMode === "saved" ? "default" : "ghost"}
                  className="h-7 px-3 text-[11px] gap-1 rounded-full"
                  onClick={() => setAngleMode("saved")}
                  disabled={createRun.isPending}
                  data-testid="toggle-angle-saved"
                >
                  Saved angle
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={angleMode === "custom" ? "default" : "ghost"}
                  className="h-7 px-3 text-[11px] gap-1 rounded-full"
                  onClick={() => setAngleMode("custom")}
                  disabled={createRun.isPending}
                  data-testid="toggle-angle-custom"
                >
                  Custom concept
                </Button>
              </div>
            </div>
            {angleMode === "saved" ? (
              <>
                <PickerSelect
                  icon={<Compass className="h-4 w-4" />}
                  label="Saved angle"
                  placeholder={
                    !selectedPersonaId
                      ? "Pick a persona first"
                      : availableAngles.length === 0
                        ? `No saved angles for this persona/${isOfferMode ? "offer" : "product"}`
                        : "Choose angle"
                  }
                  value={angleId}
                  onChange={setAngleId}
                  groups={angleGroups}
                  testId="select-angle"
                  searchable
                  wrapDescription
                  disabled={!selectedPersonaId || availableAngles.length === 0 || createRun.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Angles are filtered to the selected persona and {isOfferMode ? "offer" : "product"}.
                </p>
              </>
            ) : (
              <>
                <Textarea
                  value={customConcept}
                  onChange={(e) => setCustomConcept(e.target.value)}
                  rows={3}
                  placeholder="Describe the concept driving this video — e.g. 'busy mornings make skincare feel impossible until this one-step routine'"
                  disabled={createRun.isPending}
                  data-testid="input-custom-concept"
                />
                <p className="text-xs text-muted-foreground">
                  Your concept is turned into a saved narrative angle before the script is written.
                </p>
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              One 30s 9:16 B-roll video with an ElevenLabs voice-over and burned-in captions. The script is written and rendered automatically.
            </p>
            <Button onClick={() => createRun.mutate()} disabled={!canGenerate} data-testid="button-generate">
              {createRun.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" /> Generate video</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current run */}
      <div className="space-y-6">
        <h2 className="text-lg font-semibold">Current run</h2>
        {activeLoading && focusedRuns.length === 0 ? (
          <Skeleton className="h-40 w-full" />
        ) : focusedRuns.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
              <Clapperboard className="h-10 w-10 opacity-40" />
              <p className="text-sm">No runs yet. Pick a persona, a product or offer, and an angle above to generate your first B-roll video.</p>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="panel-current-run">
            <CardContent className="p-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {focusedRuns.map((run) => (
                  <RunVideos key={run.id} brandId={brandId!} run={run} personas={personas} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Past runs */}
      <BrollPastRuns brandId={brandId!} excludeIds={focusedRuns.map((r) => r.id)} personas={personas} />
    </div>
  );
}

// ── Past runs history ────────────────────────────────────────────────────────

function brollRunStatusMeta(status: string): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  switch (status) {
    case "completed": return { label: "Completed", variant: "default" };
    case "failed": return { label: "Failed", variant: "destructive" };
    case "partial": return { label: "Partial", variant: "outline" };
    case "pending":
    case "active":
    case "running": return { label: "Running", variant: "secondary" };
    default: return { label: status, variant: "outline" };
  }
}

function BrollPastRuns({
  brandId,
  excludeIds,
  personas,
}: {
  brandId: string;
  excludeIds: string[];
  personas: Persona[];
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data: allRuns = [], isLoading } = useQuery<BrollExpressRun[]>({
    queryKey: [`/api/brands/${brandId}/broll-express/runs`],
    enabled: !!brandId,
    staleTime: 30_000,
  });

  const excluded = new Set(excludeIds);
  const pastRuns = useMemo(
    () =>
      allRuns
        .filter((r) => !excluded.has(r.id))
        .sort((a, b) => new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRuns, excludeIds.join(",")],
  );

  return (
    <PastRunsSection title="Past runs" count={pastRuns.length} isLoading={isLoading} testIdPrefix="broll-past-runs">
      {pastRuns.map((run) => {
        const meta = brollRunStatusMeta(run.status);
        const isOpen = expandedId === run.id;
        return (
          <div key={run.id} className="rounded-lg border border-border/50 bg-card" data-testid={`card-past-run-${run.id}`}>
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : run.id)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
              data-testid={`button-toggle-past-run-${run.id}`}
            >
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`text-past-run-date-${run.id}`}>
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatRunWhen(run.createdAt as any)}
                </span>
                <Badge variant={meta.variant} className="text-[10px]" data-testid={`status-past-run-${run.id}`}>
                  {meta.label}
                </Badge>
                {run.angleName && (
                  <Badge variant="outline" className="text-[10px] max-w-[16rem] truncate">{run.angleName}</Badge>
                )}
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </button>
            {isOpen && (
              <div className="px-3 pb-3">
                <BrollPastRunVideos brandId={brandId} run={run} personas={personas} />
              </div>
            )}
          </div>
        );
      })}
    </PastRunsSection>
  );
}

// Lazily fetches ONE past run's videos when its row expands and renders them
// with the same VideoCard tiles the current run uses (view + download).
function BrollPastRunVideos({
  brandId,
  run,
  personas,
}: {
  brandId: string;
  run: BrollExpressRun;
  personas: Persona[];
}) {
  const { data: videos = [], isLoading, isError } = useQuery<BrollExpressVideo[]>({
    queryKey: [`/api/brands/${brandId}/broll-express/runs/${run.id}/videos`],
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 pt-2 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="aspect-[9/16] rounded-lg" />
      </div>
    );
  }
  if (isError) {
    return <p className="text-xs text-destructive pt-2" data-testid={`text-past-run-error-${run.id}`}>Couldn't load this run's videos.</p>;
  }
  if (videos.length === 0) {
    return <p className="text-xs text-muted-foreground pt-2" data-testid={`text-past-run-empty-${run.id}`}>No videos in this run.</p>;
  }
  return (
    <div className="grid gap-4 pt-2 sm:grid-cols-2 lg:grid-cols-3">
      {videos.map((video) => (
        <VideoCard
          key={video.id}
          brandId={brandId}
          video={video}
          persona={personas.find((p) => p.id === video.personaId)}
          angleName={run.angleName || undefined}
        />
      ))}
    </div>
  );
}

// Renders one run's videos as bare grid cells so multiple runs flatten into
// the SAME grid under "Current run". Each run keeps its own videos query +
// polling: 4s while a video is generating server-side OR its caption pass is
// still in flight (so the captioned URL swaps in when it lands).
function RunVideos({
  brandId,
  run,
  personas,
}: {
  brandId: string;
  run: BrollExpressRun;
  personas: Persona[];
}) {
  const { data: videos = [] } = useQuery<BrollExpressVideo[]>({
    queryKey: [`/api/brands/${brandId}/broll-express/runs/${run.id}/videos`],
    refetchInterval: (query) => {
      const rows = (query.state.data as BrollExpressVideo[] | undefined) ?? [];
      return rows.some(videoNeedsPolling) ? 4000 : false;
    },
  });

  return (
    <>
      {videos.map((video) => (
        <VideoCard
          key={video.id}
          brandId={brandId}
          video={video}
          persona={personas.find((p) => p.id === video.personaId)}
          angleName={run.angleName || undefined}
        />
      ))}
    </>
  );
}

function VideoCard({
  brandId,
  video,
  persona,
  angleName,
}: {
  brandId: string;
  video: BrollExpressVideo;
  persona?: Persona;
  angleName?: string;
}) {
  const { toast } = useToast();
  const meta = statusMeta(video);
  const StatusIcon = meta.icon;
  const captionsInFlight = video.status === "ready" && CAPTIONS_IN_FLIGHT.has(video.captionedVideoStatus ?? "");
  // The captioned cut replaces the raw render once it lands; caption failure
  // still leaves the uncaptioned video fully playable.
  const playbackUrl = video.captionedVideoUrl || video.videoUrl;

  const retry = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/broll-express/videos/${video.id}/retry`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/broll-express/runs/${video.runId}/videos`] });
      toast({ title: "Retrying", description: "Regenerating this video." });
    },
    onError: (e: any) => {
      toast({ title: "Retry failed", description: e?.message || "Could not retry", variant: "destructive" });
    },
  });

  return (
    <div className="overflow-hidden rounded-lg border bg-card" data-testid={`card-video-${video.id}`}>
      <div className="relative aspect-[9/16] w-full bg-muted">
        {video.status === "ready" && playbackUrl ? (
          <video
            key={playbackUrl}
            src={playbackUrl}
            controls
            playsInline
            className="h-full w-full object-cover"
            data-testid={`player-video-${video.id}`}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-4 text-center">
            {video.status === "failed" ? (
              <>
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <p className="text-xs text-muted-foreground line-clamp-3">{video.error || "Generation failed."}</p>
                <Button size="sm" variant="outline" onClick={() => retry.mutate()} disabled={retry.isPending} data-testid={`button-retry-${video.id}`}>
                  {retry.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Retry
                </Button>
              </>
            ) : (
              <>
                <StatusIcon className={`h-8 w-8 text-muted-foreground ${meta.spinning ? "animate-spin" : ""}`} />
                <p className="text-xs text-muted-foreground">{meta.label}…</p>
              </>
            )}
          </div>
        )}
        {captionsInFlight && (
          <div className="absolute bottom-2 left-2 right-2 flex justify-center">
            <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`badge-captions-${video.id}`}>
              <Loader2 className="h-3 w-3 animate-spin" /> Adding captions…
            </Badge>
          </div>
        )}
        {video.status === "ready" && video.captionedVideoUrl && (
          <div className="absolute top-2 left-2">
            <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`badge-captioned-${video.id}`}>
              <Captions className="h-3 w-3" /> Captioned
            </Badge>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 p-3">
        <div className="flex min-w-0 items-center gap-2">
          {persona?.imageUrl ? (
            <img src={thumbUrl(persona.imageUrl, 64)!} onError={thumbFallback(persona.imageUrl)} alt={persona.name} className="h-7 w-7 shrink-0 rounded-full object-cover" />
          ) : (
            <UserCircle className="h-7 w-7 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <span className="block truncate text-sm font-medium" data-testid={`text-persona-${video.id}`}>
              {persona?.name || "Persona"}
            </span>
            {angleName && (
              <span className="block truncate text-xs text-muted-foreground" data-testid={`text-angle-${video.id}`}>
                {angleName}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge variant={meta.variant} className="gap-1">
            <StatusIcon className={`h-3 w-3 ${meta.spinning ? "animate-spin" : ""}`} />
            {meta.label}
          </Badge>
          {video.status === "ready" && playbackUrl && (
            <a href={playbackUrl} download target="_blank" rel="noreferrer" data-testid={`link-download-${video.id}`}>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <Download className="h-4 w-4" />
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
